// Phone upload handoff API (desktop QR code -> photograph handwritten pages on a phone).
//
// Two credentials, deliberately kept apart:
//  - DESKTOP calls carry the app JWT (creator of the session).
//  - PHONE calls carry ONLY the handoff token, in the X-Phone-Upload-Token
//    header — never the URL, which Cloud Run and Cloudflare log in full. They
//    use `credentials: 'omit'` and no Authorization header, so a phone that
//    happens to be signed in to Jeddle never sends its own session, and a 401
//    here can never trip service.js's "clear the session and redirect" handling.
import { baseUrl } from './api';
import { getAuthToken } from '../userLocalDetails';

const SESSIONS = `${baseUrl}/api/phone-upload-sessions`;

export class PhoneUploadRequestError extends Error {
  constructor(message, status, code) {
    super(message);
    this.name = 'PhoneUploadRequestError';
    this.status = status;
    this.code = code;
  }
}

const readError = async (response) => {
  let body = null;
  try {
    body = await response.json();
  } catch (e) {
    body = null;
  }
  return new PhoneUploadRequestError(
    body?.message || `Request failed (${response.status})`,
    response.status,
    body?.code || null
  );
};

const expectJson = async (response) => {
  if (!response.ok) throw await readError(response);
  return response.json();
};

const authHeaders = () => {
  const token = getAuthToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const PHONE = `${SESSIONS}/phone`;
const phoneHeaders = (token) => ({
  'X-Phone-Upload-Token': String(token || ''),
});

// ---- desktop (app JWT) ------------------------------------------------------

export const createPhoneUploadSession = async (submissionId, questionSerial) =>
  expectJson(
    await fetch(SESSIONS, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      // Serials are strings ('1', '1a'); never let a number reach the server.
      body: JSON.stringify({
        submissionId,
        questionSerial: String(questionSerial),
      }),
    })
  );

export const getPhoneUploadSession = async (sessionId, { signal } = {}) =>
  expectJson(
    await fetch(`${SESSIONS}/${encodeURIComponent(sessionId)}`, {
      credentials: 'include',
      headers: authHeaders(),
      signal,
    })
  );

export const fetchPhoneUploadPage = async (
  sessionId,
  pageId,
  { signal } = {}
) => {
  const response = await fetch(
    `${SESSIONS}/${encodeURIComponent(sessionId)}/pages/${encodeURIComponent(
      pageId
    )}`,
    { credentials: 'include', headers: authHeaders(), signal }
  );
  if (!response.ok) throw await readError(response);
  return response.blob();
};

/**
 * Purges the session's pages from the server's holding area. Best-effort: the
 * callers never wait on it or show its failure — the server's own cleanup
 * removes whatever a failed close leaves behind. `keepalive` lets it finish if
 * the page is navigating away.
 */
export const closePhoneUploadSession = async (sessionId) => {
  const response = await fetch(`${SESSIONS}/${encodeURIComponent(sessionId)}`, {
    method: 'DELETE',
    credentials: 'include',
    headers: authHeaders(),
    keepalive: true,
  });
  if (!response.ok) throw await readError(response);
};

// ---- phone (token only) -----------------------------------------------------

export const getPhoneUploadContext = async (token, { signal } = {}) =>
  expectJson(
    await fetch(PHONE, {
      credentials: 'omit',
      headers: phoneHeaders(token),
      signal,
    })
  );

export const uploadPhonePage = async (token, file, { signal } = {}) => {
  const body = new FormData();
  body.append('file', file, file?.name || 'page.jpg');
  return expectJson(
    await fetch(`${PHONE}/pages`, {
      method: 'POST',
      credentials: 'omit',
      headers: phoneHeaders(token),
      body,
      signal,
    })
  );
};

export const deletePhonePage = async (token, pageId) =>
  expectJson(
    await fetch(`${PHONE}/pages/${encodeURIComponent(pageId)}`, {
      method: 'DELETE',
      credentials: 'omit',
      headers: phoneHeaders(token),
    })
  );

// ---- links ------------------------------------------------------------------

export const PHONE_UPLOAD_ROUTE_PREFIX = '/m/upload/';

/**
 * The phone must open the SAME frontend the desktop is on (same API backend,
 * same flag state), so the page's own origin is the source of truth — the
 * convention the existing share links follow (ShareOrDownloadControl,
 * TaskCard). REACT_APP_SELF_BASE_URL is only a fallback: several build scripts
 * still bake stale Cloud Run hosts into it.
 */
export const phoneUploadLink = (token) => {
  const origin =
    (typeof window !== 'undefined' && window.location?.origin) ||
    process.env.REACT_APP_SELF_BASE_URL ||
    '';
  return `${origin.replace(/\/+$/, '')}/#${PHONE_UPLOAD_ROUTE_PREFIX}${token}`;
};

/** The token in a `#/m/upload/<token>` hash, or null when this is not that route. */
export const phoneUploadTokenFromHash = (hash) => {
  const path = String(hash || '')
    .replace(/^#/, '')
    .split('?')[0];
  if (!path.startsWith(PHONE_UPLOAD_ROUTE_PREFIX)) return null;
  const token = path.slice(PHONE_UPLOAD_ROUTE_PREFIX.length).split('/')[0];
  return token || null;
};
